'use client';
import { useEffect, useReducer, useState } from 'react';

import { useLazyQuery, useMutation } from '@lichtblick/apollo';
import { useTranslation } from '@lichtblick/i18next';
import { logger } from '@lichtblick/logger';
import {
  AppointmentFormErrors,
  AppointmentType,
  AppointmentTypeSelection,
  AppointmentTypeSelectionNullObject,
  GetAppointment_V1Query,
  AppointmentReducerState,
  Option,
  PostAppointment_V1Mutation,
  PostAppointment_V1MutationVariables,
  PatchAppointmentComposite_V1Mutation,
  PatchAppointmentComposite_V1MutationVariables,
  PatchBlocker_V1MutationVariables,
  PostBlocker_V1Mutation,
  PostBlocker_V1MutationVariables,
  PatchBlocker_V1Mutation,
  GET_SINGLE_ORDER,
  GetSingleWorkOrderQuery,
  QueryGetWorkOrder_V1Args,
  AppointmentCalendarItemPayloadModel,
} from '@lichtblick/types';
import { appointmentToAppointmentForm, formatName } from '@lichtblick/utils';

import { AppointmentActions, appointmentDefaultState, AppointmentReducer } from './AppointmentReducer';
import { CalendarEvent } from './type';
import { parseEditingValue, resolveCreatingMode, useAppointmentQueryState } from './useAppointmentQueryState';

import {
  CREATE_APPOINTMENT_CALENDAR,
  CREATE_BLOCKER_CALENDAR,
  UPDATE_APPOINTMENT_CALENDAR,
  UPDATE_BLOCKER_CALENDAR,
  DELETE_APPOINTMENT_CALENDAR,
  DELETE_BLOCKER_CALENDAR,
} from '@/graphql/pm/appointment';
import { useOptions } from '@/hooks/pm/useOptions';
import { useDebouncedFunction } from '@/hooks/useDebouncedFunction';

export type ItemEvent =
  | { calendarEvent: CalendarEvent | undefined; currentError: string | undefined; isCreated: boolean | undefined }
  | undefined;

export type AppointmentDrawerHookProps = {
  appointment?: GetAppointment_V1Query['getAppointment_V1']['data'];
  initialFormState?: AppointmentReducerState;
  onClose: () => void;
  onDelete?: (id: number) => void;
  onSave?: (itemEvent?: ItemEvent) => void;
};

export const useAppointmentDrawer = ({
  appointment,
  initialFormState,
  onClose,
  onDelete,
  onSave,
}: AppointmentDrawerHookProps) => {
  const SAVE_TIMEOUT_MS = 60000;
  const { t } = useTranslation();
  const { isCreating, isEditing } = useAppointmentQueryState();
  const workOrderParam = resolveCreatingMode(isCreating);
  const parsedEditing = parseEditingValue(isEditing);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [shouldShowToast, setShouldShowToast] = useState(false);
  const [toastTitle, setToastTitle] = useState<string>(t('pm:snackbarCalendar.errorTitle'));
  const [formErrors, setFormErrors] = useState<AppointmentFormErrors>({});
  const [mandatoryFields, setMandatoryFields] = useState<(keyof AppointmentFormErrors)[]>([]);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [appointmentTypeName, setAppointmentTypeName] = useState<string>('');

  useEffect(() => {
    logger.debug('Appointment drawer hook state changed', {
      component: 'useAppointmentDrawer',
      isCreating,
      isEditing,
      parsedEditingId: parsedEditing.id,
      parsedEditingIsBlocker: parsedEditing.isBlocker,
      hasAppointment: Boolean(appointment),
      hasInitialFormState: Boolean(initialFormState),
    });
  }, [appointment, initialFormState, isCreating, isEditing, parsedEditing.id, parsedEditing.isBlocker]);

  const { debouncedFunction: debouncedSetSearch } = useDebouncedFunction();
  const [createAppointment] = useMutation<PostAppointment_V1Mutation, PostAppointment_V1MutationVariables>(
    CREATE_APPOINTMENT_CALENDAR,
  );
  const [createBlocker] = useMutation<PostBlocker_V1Mutation, PostBlocker_V1MutationVariables>(CREATE_BLOCKER_CALENDAR);
  const [updateBlocker] = useMutation<PatchBlocker_V1Mutation, PatchBlocker_V1MutationVariables>(
    UPDATE_BLOCKER_CALENDAR,
  );
  const [updateAppointment] = useMutation<
    PatchAppointmentComposite_V1Mutation,
    PatchAppointmentComposite_V1MutationVariables
  >(UPDATE_APPOINTMENT_CALENDAR);
  const [deleteBlocker] = useMutation(DELETE_BLOCKER_CALENDAR); // Todo: Type from backend is wrong
  const [deleteAppointment] = useMutation(DELETE_APPOINTMENT_CALENDAR); // Todo: Type from backend is wrong

  const [appointmentState, appointmentDispatch] = useReducer(AppointmentReducer, appointmentDefaultState);
  const currentSelection = appointmentState.appointmentType ?? AppointmentTypeSelectionNullObject;

  const {
    appointmentTypeOptions,
    checkIsNotTypes,
    checkIsType,
    craftmanListOptions,
    filteredBranchOptions,
    filteredPartnerOptions,
    filteredTeamOptions,
    filteredWorkOrderOptions,
    setAppointmentTypeQuery,
    setBranchQuery,
    setCraftmanQuery,
    setPartnerQuery,
    setTeamQuery,
    setWorkOrderQuery,
  } = useOptions();

  const resetForm = () => {
    setBranchQuery('');
    setCraftmanQuery('');
    setPartnerQuery('');
    setTeamQuery('');
    setWorkOrderQuery('');
    appointmentDispatch({
      type: AppointmentActions.RESET,
    });
  };

  const [getWorkOrder] = useLazyQuery<GetSingleWorkOrderQuery, QueryGetWorkOrder_V1Args>(GET_SINGLE_ORDER, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const formPreload = initialFormState
      ? initialFormState
      : appointment
        ? appointmentToAppointmentForm(appointment)
        : {
            ...appointmentDefaultState,
          };

    appointmentDispatch({ type: AppointmentActions.SET_APPOINTMENT, payload: formPreload });

    logger.debug('Appointment drawer form preloaded', {
      component: 'useAppointmentDrawer',
      source: initialFormState ? 'initialFormState' : appointment ? 'appointment' : 'default',
      appointmentId: appointment?.appointment?.id,
      workOrderId: formPreload.workOrder,
      appointmentType: formPreload.appointmentType,
      branchId: formPreload.branch,
    });

    const getWorkOrderName = async (formPreload: AppointmentReducerState) => {
      setIsLoading(true);

      if (formPreload.workOrder && formPreload.workOrder !== -1) {
        const workOrder = await getWorkOrder({
          variables: {
            input: {
              workOrderId: formPreload.workOrder,
            },
          },
        });

        if (workOrder.data?.getWorkOrder_V1.data) {
          if (workOrder.data.getWorkOrder_V1.data?.name) {
            setWorkOrderQuery(workOrder.data.getWorkOrder_V1.data.name);
          }

          logger.debug('Appointment drawer work order lookup completed', {
            component: 'useAppointmentDrawer',
            appointmentId: appointment?.appointment?.id,
            workOrderId: formPreload.workOrder,
            resolvedWorkOrderName: workOrder.data.getWorkOrder_V1.data?.name ?? null,
            shouldFetchWorkOrderDetails: Boolean(
              workOrder.data.getWorkOrder_V1.data?.id && workOrderParam.mode === 'workOrder',
            ),
          });

          if (workOrder.data.getWorkOrder_V1.data?.id && workOrderParam.mode === 'workOrder') {
            fetchDataFromWorkOrder(workOrder.data.getWorkOrder_V1.data?.id, formPreload);
          }
        }
      } else if (appointment?.appointment) {
        setWorkOrderQuery(appointment.appointment.workOrder?.name ?? '');
      }

      setIsLoading(false);
    };

    getWorkOrderName(formPreload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment, initialFormState]);

  useEffect(() => {
    if (appointment) {
      const workflowDefinitionId = appointment.appointment?.workflowDefinition?.id;
      const matchingOption = appointmentTypeOptions.find((ap: Option<AppointmentTypeSelection>) => {
        const optionValue = ap.value;

        if (typeof optionValue !== 'object' || optionValue === null) {
          return false;
        }

        return optionValue.id === workflowDefinitionId;
      });
      const matchingBlockerOption = appointmentTypeOptions.find((ap: Option<AppointmentTypeSelection>) => {
        const optionValue = ap.value;

        if (typeof optionValue !== 'object' || optionValue === null) {
          return false;
        }

        return optionValue.id === workflowDefinitionId && optionValue.kind === 'blocker';
      });

      setAppointmentTypeName(matchingOption?.label ?? matchingBlockerOption?.label ?? '');

      logger.debug('Appointment drawer appointment type label resolved', {
        component: 'useAppointmentDrawer',
        appointmentId: appointment?.appointment?.id,
        workflowDefinitionId,
        resolvedAppointmentTypeName: matchingOption?.label ?? matchingBlockerOption?.label ?? '',
      });
    }
  }, [appointment, appointmentTypeOptions]);

  useEffect(() => {
    if (appointmentState.branch && appointmentState.branch !== -1 && filteredBranchOptions.length > 0) {
      const matchingBranch = filteredBranchOptions.find((opt: Option) => opt.value === appointmentState.branch);

      if (matchingBranch?.label && matchingBranch.label !== '') {
        setBranchQuery(matchingBranch.label);
      }
    } else if (!appointmentState.branch || appointmentState.branch === -1) {
      setBranchQuery('');
    }
  }, [appointmentState.branch, filteredBranchOptions, setBranchQuery]);

  const calculateMandatoryFields = (appointmentType: AppointmentTypeSelection = AppointmentTypeSelectionNullObject) => {
    logger.debug('Appointment drawer mandatory field rules evaluated', {
      component: 'useAppointmentDrawer',
      appointmentType,
      appointmentTypeName,
      isBlocker: parsedEditing.isBlocker,
    });

    if (appointmentType !== null) {
      if (checkIsType(appointmentType, [AppointmentType.VorOrtTerminWP, AppointmentType.VorOrtTermin])) {
        setMandatoryFields(['workOrder', 'startDate', 'fromTime', 'toTime']);
      } else if (
        checkIsType(appointmentType, [
          AppointmentType.Feiertag,
          AppointmentType.Puffertag,
          AppointmentType.Schulung,
          AppointmentType.Urlaub,
          AppointmentType.Krank,
          AppointmentType.BueroTag,
        ])
      ) {
        setMandatoryFields(['startDate', 'endDate']);
      } else if (
        checkIsType(appointmentType, [
          AppointmentType.WarenanlieferungWP,
          AppointmentType.VorarbeitMontageWP,
          AppointmentType.MontageWP,
          AppointmentType.NachbesserungWP,
          AppointmentType.GeruestAbbau,
          AppointmentType.GeruestAufbau,
          AppointmentType.ACMontageterminVerlaengert,
          AppointmentType.ACNachbesserungsterminAGVerschuldet,
          AppointmentType.ACNachbesserungsterminAGVerschuldet,
          AppointmentType.ACNachbesserungsterminInSeVerschuldet,
          AppointmentType.ACNachbesserungsterminKundeVerschuldet,
          AppointmentType.DCMontageterminVerlaengert,
          AppointmentType.DCNachbesserungsterminAGVerschuldet,
          AppointmentType.DCNachbesserungsterminInSeVerschuldet,
          AppointmentType.DCNachbesserungsterminKundeVerschuldet,
          AppointmentType.ACMontagetermin,
          AppointmentType.DCMontagetermin,
        ])
      ) {
        setMandatoryFields(['workOrder', 'startDate', 'endDate']);
      } else if (
        checkIsType(appointmentType, [
          AppointmentType.ZaehlerSetzungstermin,
          AppointmentType.Warenlieferung,
          AppointmentType.KickoffCall,
          AppointmentType.OnlineBegehung,
        ])
      ) {
        setMandatoryFields(['workOrder', 'startDate']);
      }
    }
  };

  const isBlocker =
    parsedEditing.isBlocker ||
    appointmentState.appointmentType?.kind === 'blocker' ||
    [
      AppointmentType.Krank,
      AppointmentType.Feiertag,
      AppointmentType.Puffertag,
      AppointmentType.Schulung,
      AppointmentType.Urlaub,
      AppointmentType.BueroTag,
    ].includes(appointmentTypeName as AppointmentType);

  const calculateAppointmentFormErrors = (): AppointmentFormErrors => {
    const errors: AppointmentFormErrors = {};
    const allMandatoryFields = ['appointmentType', ...mandatoryFields];

    if (isBlocker) {
      allMandatoryFields.push('people');
    }

    for (const key of allMandatoryFields) {
      const errorKey = key as keyof AppointmentFormErrors;

      if (key === 'fromTime' || key === 'toTime') {
        if (
          !appointmentState['startDate'] ||
          (appointmentState['startDate'].getHours() == 0 && appointmentState['startDate'].getMinutes() == 0)
        ) {
          errors[errorKey] = `${t(`pm:${key}`)} ${t('pm:isMandatory')}`;
        }
      } else if (
        appointmentState[key as keyof AppointmentReducerState] !== 0 &&
        (!appointmentState[key as keyof AppointmentReducerState] ||
          appointmentState[key as keyof AppointmentReducerState] === -1)
      ) {
        errors[errorKey] = `${t(`pm:${key}`)} ${t('pm:isMandatory')}`;
      }
    }

    return errors;
  };

  const onRemoveTeam = (team?: Option) => {
    if (team) {
      const teamsAux = appointmentState.teams ? [...appointmentState.teams] : [];
      const idxToRemove = teamsAux.findIndex((el) => el.value === team.value);

      if (idxToRemove > -1) {
        teamsAux.splice(idxToRemove, 1);
        appointmentDispatch({ type: AppointmentActions.SET_TEAMS, payload: teamsAux });
      }
    }
  };

  const onRemovePerson = (person?: Option) => {
    if (person) {
      const peopleAux = appointmentState.people ? [...appointmentState.people] : [];
      const idxToRemove = peopleAux.findIndex((el) => el.value === person.value);

      if (idxToRemove > -1) {
        peopleAux.splice(idxToRemove, 1);
        appointmentDispatch({ type: AppointmentActions.SET_PEOPLE, payload: peopleAux });
      }
    }
  };

  const updatePartnerCompanyIds = (
    previousPartner: number | null | undefined,
    currentPartner: number | null | undefined,
  ) => {
    const partnerCompanyIdsToAssign: number[] = [];
    const partnerCompanyIdsToUnassign: number[] = [];

    if (previousPartner !== currentPartner) {
      if (previousPartner !== null && previousPartner !== undefined) {
        partnerCompanyIdsToUnassign.push(previousPartner);
      }

      if (currentPartner !== null && currentPartner !== undefined) {
        partnerCompanyIdsToAssign.push(currentPartner);
      }
    }

    return { partnerCompanyIdsToAssign, partnerCompanyIdsToUnassign };
  };

  const onAppointmentTypeChange = (selection: AppointmentTypeSelection = AppointmentTypeSelectionNullObject) => {
    const isNewChoiceBlocker = checkIsType(selection, [
      AppointmentType.Krank,
      AppointmentType.Feiertag,
      AppointmentType.Puffertag,
      AppointmentType.Schulung,
      AppointmentType.Urlaub,
      AppointmentType.BueroTag,
    ]);

    logger.debug('Appointment drawer type changed', {
      component: 'useAppointmentDrawer',
      selection,
      isNewChoiceBlocker,
      parsedEditingId: parsedEditing.id,
      parsedEditingIsBlocker: parsedEditing.isBlocker,
    });

    try {
      if (isNewChoiceBlocker) {
        if (appointmentState.people.length) {
          appointmentDispatch({ type: AppointmentActions.SET_PEOPLE, payload: [] });
        }

        if (appointmentState.teams?.length) {
          appointmentDispatch({ type: AppointmentActions.SET_TEAMS, payload: [] });
        }
      }
    } finally {
      appointmentDispatch({ type: AppointmentActions.SET_TYPE, payload: selection });
    }
  };

  const updateTeamIds = (previousTeams: number[], currentTeams: number[]) => {
    const teamIdToAssign: number[] = [];
    const teamIdToUnassign: number[] = [];

    const previousTeamSet = new Set(previousTeams);
    const currentTeamSet = new Set(currentTeams);

    currentTeams.forEach((team) => {
      if (!previousTeamSet.has(team)) {
        teamIdToAssign.push(team);
      }
    });

    previousTeams.forEach((team) => {
      if (!currentTeamSet.has(team)) {
        teamIdToUnassign.push(team);
      }
    });

    return { teamIdToAssign, teamIdToUnassign };
  };

  const updateUserIds = (previousUsers: number[], currentUsers: number[]) => {
    const userIdToAssign: number[] = [];
    const userIdToUnassign: number[] = [];

    const previousUserSet = new Set(previousUsers);
    const currentUserSet = new Set(currentUsers);

    currentUsers.forEach((user) => {
      if (!previousUserSet.has(user)) {
        userIdToAssign.push(user);
      }
    });

    previousUsers.forEach((user) => {
      if (!currentUserSet.has(user)) {
        userIdToUnassign.push(user);
      }
    });

    return { userIdToAssign, userIdToUnassign };
  };

  const handleAppointment = async () => {
    let calendarEvent: CalendarEvent | undefined = undefined;
    let isCreated = true;
    let currentError: string | undefined = '';
    const logContext = {
      component: 'AppointmentHandler',
      operation: appointment?.appointment?.id ? 'updateAppointment' : 'createAppointment',
      appointmentId: appointment?.appointment?.id,
      workOrderId: appointmentState.workOrder,
      appointmentType: appointmentState.appointmentType,
    };
    const formErrors = calculateAppointmentFormErrors();

    setFormErrors(formErrors);

    if (Object.keys(formErrors).length) {
      return { calendarEvent: undefined, isCreated: undefined, currentError: 'MANDATORY_FIELD_MISSING' };
    }

    const isSingleDay = checkIsType(currentSelection, [
      AppointmentType.Warenlieferung,
      AppointmentType.VorOrtTerminWP,
      AppointmentType.ZaehlerSetzungstermin,
      AppointmentType.KickoffCall,
      AppointmentType.OnlineBegehung,
      AppointmentType.VorOrtTermin,
    ]);

    logger.debug('Appointment drawer save started', {
      component: 'useAppointmentDrawer',
      isBlocker,
      isCreating: !appointment?.appointment?.id,
      appointmentId: appointment?.appointment?.id,
      appointmentType: appointmentState.appointmentType,
      workOrderId: appointmentState.workOrder,
      branchId: appointmentState.branch,
      peopleCount: appointmentState.people.length,
      teamCount: appointmentState.teams?.length ?? 0,
    });

    try {
      const { partnerCompanyIdsToAssign, partnerCompanyIdsToUnassign } = updatePartnerCompanyIds(
        null,
        appointmentState.partner,
      );

      const { teamIdToAssign, teamIdToUnassign } = updateTeamIds(
        appointment?.appointment?.constructedByTeams?.map((team) => team.team?.id ?? -1) ?? [],
        appointmentState.teams?.map((team) => Number(team.value)) ?? [],
      );

      const { userIdToAssign, userIdToUnassign } = updateUserIds(
        appointment?.appointment?.constructedByCraftsmen?.map((user) => user?.craftsman?.id ?? -1) ?? [],
        appointmentState.people?.map((user) => Number(user.value)) ?? [],
      );

      if (appointment && appointment.appointment?.id) {
        isCreated = false;
        const response = await updateAppointment({
          variables: {
            input: {
              appointmentId: appointment.appointment?.id,
              branchOfficeId: appointmentState.branch,
              comment: appointmentState.note ?? '',
              endAtUtc: appointmentState.endDate
                ? appointmentState.endDate
                : isSingleDay
                  ? appointmentState.startDate
                  : appointmentState.endDate,
              isBinding: appointmentState.isBinding,
              partnerCompanyIdsToAssign: partnerCompanyIdsToAssign,
              partnerCompanyIdsToUnassign: partnerCompanyIdsToUnassign,
              startAtUtc: appointmentState.startDate,
              teamIdToAssign: teamIdToAssign,
              teamIdToUnassign: teamIdToUnassign,
              userIdsToAssign: userIdToAssign,
              userIdsToUnassign: userIdToUnassign,
            },
          },
        });

        calendarEvent =
          response.data?.patchAppointmentComposite_V1.error === 'NONE'
            ? {
                end: appointmentState.endDate
                  ? new Date(appointmentState.endDate.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }))
                  : new Date(),
                id: appointment?.appointment?.id ?? -1,
                name: appointment?.appointment?.workOrder?.name ?? '',
                type: appointment?.appointment?.workflowDefinition?.name ?? '',
                start: appointmentState.startDate
                  ? new Date(appointmentState.startDate.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }))
                  : new Date(),
                teamName: appointmentState.teams?.[0]?.label ?? '',
                isBinding: appointmentState.isBinding,
                workOrderId:
                  appointmentState.workOrder !== -1
                    ? appointmentState.workOrder
                    : appointment?.appointment?.workOrder?.id,
              }
            : undefined;

        currentError = response.data?.patchAppointmentComposite_V1.error;
      } else {
        const shouldAutoCreateNewWorkflowDefinitionInstance = checkIsType(currentSelection, [
          AppointmentType.ACNachbesserungsterminAGVerschuldet,
          AppointmentType.ACNachbesserungsterminInSeVerschuldet,
          AppointmentType.ACNachbesserungsterminKundeVerschuldet,
          AppointmentType.DCNachbesserungsterminAGVerschuldet,
          AppointmentType.DCNachbesserungsterminInSeVerschuldet,
          AppointmentType.DCNachbesserungsterminKundeVerschuldet,
        ]);

        const newAppointmentResponse = await createAppointment({
          variables: {
            input: {
              workflowDefinitionId: appointmentState.appointmentType?.id ?? -1,
              workOrderId: appointmentState.workOrder,
              startAtUtc: appointmentState.startDate,
              endAtUtc: appointmentState.endDate
                ? appointmentState.endDate
                : isSingleDay
                  ? appointmentState.startDate
                  : appointmentState.endDate,
              teamIds: appointmentState.teams?.map((team) => Number(team.value)),
              partnerCompanyIds:
                appointmentState.partner && appointmentState.partner !== -1 ? [appointmentState.partner] : undefined,
              branchOfficeId: appointmentState.branch,
              userIds: appointmentState.people.map((person) => Number(person.value)),
              isBinding: appointmentState.isBinding,
              comment: appointmentState.note ?? '',
              createNewWorkflowDefinitionInstance:
                appointmentState.createNewWorkflowInstance || shouldAutoCreateNewWorkflowDefinitionInstance,
            },
          },
        });
        const newAppointment = newAppointmentResponse.data?.postAppointment_V1
          .data as AppointmentCalendarItemPayloadModel;

        if (
          !newAppointment ||
          newAppointmentResponse.data?.postAppointment_V1.error !== 'NONE' ||
          (newAppointmentResponse.errors && newAppointmentResponse.errors?.length > 0)
        ) {
          currentError = newAppointmentResponse.data?.postAppointment_V1.error;

          return { calendarEvent: undefined, isCreated, currentError };
        }

        calendarEvent = {
          end: new Date(new Date(newAppointment.endAtUtc).toLocaleString('en-US', { timeZone: 'Europe/Berlin' })),
          id: newAppointment.id ?? -1,
          name: newAppointment.workOrder?.name ?? '',
          type: newAppointment.workflowDefinition?.name ?? '',
          start: new Date(new Date(newAppointment.startAtUtc).toLocaleString('en-US', { timeZone: 'Europe/Berlin' })),
          teamName: newAppointment.teams?.[0]?.name ?? '',
          isBinding: newAppointment.isBinding ?? appointmentState.isBinding ?? undefined,
          workOrderId: newAppointment.workOrder?.id ?? undefined,
        };
      }

      return { calendarEvent, isCreated, currentError };
    } catch (error) {
      logger.error('Unexpected error handling appointment', {
        ...logContext,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });

      return { calendarEvent: undefined, isCreated, currentError: undefined };
    }
  };

  const handleBlocker = async () => {
    const formErrors = calculateAppointmentFormErrors();
    const logContext = {
      component: 'BlockerHandler',
      operation: appointment?.appointment?.id ? 'updateBlocker' : 'createBlocker',
      appointmentId: appointment?.appointment?.id,
      appointmentType: appointmentState.appointmentType,
    };

    setFormErrors(formErrors);

    if (Object.keys(formErrors).length) {
      return { calendarEvent: undefined, isCreated: undefined, currentError: 'MANDATORY_FIELD_MISSING' };
    }

    let calendarEvent: CalendarEvent | undefined = undefined;
    let isCreated = true;
    let currentError: string | undefined = '';

    logger.debug('Appointment drawer blocker save started', {
      component: 'useAppointmentDrawer',
      isCreating: !appointment?.appointment?.id,
      appointmentId: appointment?.appointment?.id,
      appointmentType: appointmentState.appointmentType,
      workOrderId: appointmentState.workOrder,
      branchId: appointmentState.branch,
    });

    try {
      if (appointment && appointment.appointment?.id) {
        isCreated = false;
        const response = await updateBlocker({
          variables: {
            input: {
              blockerId: appointment?.appointment?.id,
              branchOfficeId: appointmentState.branch !== -1 ? appointmentState.branch : undefined,
              comment: appointmentState.note ?? '',
              endAtUtc: appointmentState.endDate,
              partnerCompanyId: appointmentState.partner !== -1 ? appointmentState.partner : undefined,
              reasonId: appointmentState.appointmentType?.id ?? -1,
              startAtUtc: appointmentState.startDate,
              teamId: appointmentState.teams?.[0]?.value ? Number(appointmentState.teams?.[0]?.value) : undefined,
              userId: appointmentState.people?.[0]?.value ? Number(appointmentState.people?.[0]?.value) : undefined,
            },
          },
        });

        calendarEvent =
          response.data?.patchBlocker_V1.error === 'NONE'
            ? {
                end: appointmentState.endDate
                  ? new Date(appointmentState.endDate.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }))
                  : new Date(),
                id: appointment?.appointment?.id ?? -1,
                name: '',
                type: response.data?.patchBlocker_V1.data?.reason?.title ?? '',
                start: appointmentState.startDate
                  ? new Date(appointmentState.startDate.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }))
                  : new Date(),
                teamName: appointmentState.teams?.[0]?.label ?? '',
              }
            : undefined;
        currentError = response.data?.patchBlocker_V1.error;
      } else {
        const newBlockerResponse = await createBlocker({
          variables: {
            input: {
              branchOfficeId: appointmentState.branch === -1 ? undefined : appointmentState.branch,
              comment: appointmentState.note ?? '',
              endAtUtc: appointmentState.endDate,
              partnerCompanyId: appointmentState.partner !== -1 ? appointmentState.partner : undefined,
              reasonId: appointmentState.appointmentType?.id ?? -1,
              startAtUtc: appointmentState.startDate,
              teamId: appointmentState.teams?.[0]?.value ? Number(appointmentState.teams?.[0]?.value) : undefined,
              userId: appointmentState.people?.[0]?.value ? Number(appointmentState.people?.[0]?.value) : undefined,
            },
          },
        });
        const newBlocker = newBlockerResponse.data?.postBlocker_V1.data;

        if (
          !newBlocker ||
          newBlockerResponse.data?.postBlocker_V1.error !== 'NONE' ||
          (newBlockerResponse.errors && newBlockerResponse.errors?.length > 0)
        ) {
          currentError = newBlockerResponse.data?.postBlocker_V1.error;

          return;
        }

        calendarEvent = {
          end: new Date(new Date(newBlocker.endAtUtc).toLocaleString('en-US', { timeZone: 'Europe/Berlin' })),
          id: newBlocker.id ?? -1,
          name: '',
          type: newBlocker.reason?.title ?? '',
          start: new Date(new Date(newBlocker.startAtUtc).toLocaleString('en-US', { timeZone: 'Europe/Berlin' })),
          teamName: appointmentState.teams?.[0]?.label ?? '',
        };
      }

      logger.debug('Appointment drawer blocker save completed', {
        component: 'useAppointmentDrawer',
        isCreated,
        calendarEventId: calendarEvent?.id,
        currentError,
      });

      return { calendarEvent, isCreated, currentError };
    } catch (error) {
      logger.error('Unexpected error handling blocker', {
        ...logContext,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });

      return { calendarEvent: undefined, isCreated, currentError: undefined };
    }
  };

  const onAcceptHandler = async () => {
    if (isSaving) {
      return;
    }

    const isCreated = !appointment?.appointment?.id;

    setIsSaving(true);
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    let item: ItemEvent = { calendarEvent: undefined, isCreated: undefined, currentError: undefined };

    try {
      const saveAction = isBlocker ? handleBlocker() : handleAppointment();
      const timeoutAction = new Promise<ItemEvent>((resolve) => {
        timeoutHandle = setTimeout(() => {
          resolve({ calendarEvent: undefined, isCreated, currentError: 'TIMEOUT' });
        }, SAVE_TIMEOUT_MS);
      });

      item = await Promise.race([saveAction, timeoutAction]);
    } finally {
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }

      setIsSaving(false);
    }

    if (!item || !item?.calendarEvent) {
      const currentError = item?.currentError ?? 'UNKNOWN';

      setToastMessage(
        `${t(`pm:snackbarCalendar.${item?.isCreated ? 'messageFailedCreated' : 'messageFailedUpdated'}`)}. ${t('pm:snackbarCalendar.reason')}: ${t(`pm:snackbarCalendar.errorReason.${currentError}`)}`,
      );
      setToastTitle(t('pm:snackbarCalendar.errorTitle'));
      setShouldShowToast(true);

      return;
    }

    onSave?.(item);
    setFormErrors({});
    setAppointmentTypeName('');
    onClose();
  };

  const handleDeleteAppointment = async () => {
    const logContext = {
      component: 'AppointmentHandler',
      operation: 'deleteAppointment',
      appointmentId: appointment?.appointment?.id,
      appointmentType: appointmentState.appointmentType,
      workOrder: appointmentState.workOrder,
      branch: appointmentState.branch,
    };

    if (
      !appointmentState.appointmentType ||
      appointmentState.appointmentType.id === -1 ||
      appointmentState.workOrder === -1 ||
      appointmentState.branch === -1 ||
      !appointmentState.startDate ||
      !appointmentState.endDate
    ) {
      return;
    }

    let currentError: string | undefined = '';

    try {
      if (appointment && appointment.appointment?.id) {
        const response = await deleteAppointment({
          variables: {
            input: {
              appointmentId: appointment.appointment?.id,
            },
          },
        });

        currentError = response.data?.deleteAppointment_V1?.error;

        return { deletedId: appointment.appointment.id, currentError };
      } else {
        return { deletedId: undefined, currentError: undefined };
      }
    } catch (error) {
      logger.error('Unexpected error during appointment deletion', {
        ...logContext,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });

      return { deletedId: undefined, currentError: undefined };
    }
  };

  const handleDeleteBlocker = async () => {
    const logContext = {
      component: 'BlockerHandler',
      operation: 'deleteBlocker',
      appointmentId: appointment?.appointment?.id,
      appointmentType: appointmentState.appointmentType,
    };

    if (
      !appointmentState.appointmentType ||
      appointmentState.appointmentType.id === -1 ||
      !appointmentState.startDate ||
      !appointmentState.endDate
    ) {
      return;
    }

    let currentError: string | undefined = '';

    try {
      if (appointment && appointment.appointment?.id) {
        const response = await deleteBlocker({
          variables: {
            input: {
              blockerId: appointment?.appointment?.id,
            },
          },
        });

        currentError = response.data?.deleteBlocker_V1?.error;

        return { deletedId: appointment.appointment.id, currentError };
      } else {
        return { deletedId: undefined, currentError: undefined };
      }
    } catch (error) {
      logger.error('Unexpected error during blocker deletion', {
        ...logContext,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });

      return { deletedId: undefined, currentError: undefined };
    }
  };

  const onDeleteHandler = async () => {
    if (isSaving) {
      return;
    }

    setIsLoading(true);
    let item: { currentError: string | undefined; deletedId: number | undefined } | undefined = {
      deletedId: undefined,
      currentError: undefined,
    };

    if (isBlocker) {
      item = await handleDeleteBlocker();
    } else {
      item = await handleDeleteAppointment();
    }

    if (!item || !item?.deletedId || (item.currentError && item.currentError !== 'NONE')) {
      const currentError = item?.currentError ?? 'UNKNOWN';

      setToastMessage(
        `${t(`pm:snackbarCalendar.messageFailedDeleted`)}. ${t('pm:snackbarCalendar.reason')}: ${t(`pm:snackbarCalendar.errorReason.${currentError}`)}`,
      );
      setToastTitle(t('pm:snackbarCalendar.errorTitle'));
      setShouldShowToast(true);
      setIsLoading(false);

      return;
    }

    onDelete?.(item?.deletedId);
    setIsLoading(false);
    setFormErrors({});
    setAppointmentTypeName('');
    onClose();
  };

  const fetchDataFromWorkOrder = async (e: string | number | undefined, formPreload?: AppointmentReducerState) => {
    if (e) {
      setIsLoading(true);
      const workOrder = await getWorkOrder({
        variables: {
          input: {
            workOrderId: parseInt(e.toString()),
          },
        },
      });
      let toastMessage = '';

      if (workOrder.data?.getWorkOrder_V1?.data?.branchOffice) {
        appointmentDispatch({
          type: AppointmentActions.SET_BRANCH,
          payload: parseInt(workOrder.data?.getWorkOrder_V1.data.branchOffice.id.toString()),
        });
        setBranchQuery(workOrder.data?.getWorkOrder_V1.data.branchOffice?.name ?? '');

        toastMessage += t('pm:snackbarCalendar.autoloadBranchSuccess');
      } else {
        toastMessage += t('pm:snackbarCalendar.autoloadBranchFailure');
      }

      toastMessage += '\n';

      if (workOrder.data?.getWorkOrder_V1?.data?.projectManager) {
        const peopleManger = {
          value: parseInt(workOrder.data?.getWorkOrder_V1.data.projectManager.id.toString()),
          label: formatName(workOrder.data?.getWorkOrder_V1.data.projectManager),
        };
        let payload: Option[] = [peopleManger];

        if (formPreload) {
          const isProjectManagerIncluded = formPreload.people.some(
            (person) => person.value === workOrder.data?.getWorkOrder_V1.data?.projectManager?.id.toString(),
          );

          payload = isProjectManagerIncluded ? formPreload.people : [...formPreload.people, peopleManger];
        }

        appointmentDispatch({
          type: AppointmentActions.SET_PEOPLE,
          payload: payload,
        });

        toastMessage += t('pm:snackbarCalendar.autoloadProjectManagerSuccess');
      } else {
        toastMessage += t('pm:snackbarCalendar.autoloadProjectManagerFailure');
      }

      setToastTitle(t('pm:snackbarCalendar.infoTitle'));
      setToastMessage(toastMessage);
      setShouldShowToast(true);
    }

    setIsLoading(false);
  };

  const handleDrawerClose = () => {
    if (isSaving) {
      return;
    }

    onClose();
    setFormErrors({});
    setMandatoryFields([]);
    setAppointmentTypeName('');
    setShouldShowToast(false);
    appointmentDispatch({ type: AppointmentActions.SET_APPOINTMENT, payload: appointmentDefaultState });
  };

  return {
    // Drawer-level state and handlers
    isLoading,
    isSaving,
    shouldShowToast,
    toastMessage,
    toastTitle,
    setShouldShowToast,
    onAcceptHandler,
    onDeleteHandler,
    handleDrawerClose,
    resetForm,

    // Form state
    appointment,
    appointmentState,
    appointmentDispatch,
    formErrors,
    setFormErrors,
    mandatoryFields,
    appointmentTypeName,
    setAppointmentTypeName,
    isBlocker,

    // Options and queries
    appointmentTypeOptions,
    checkIsNotTypes,
    checkIsType,
    craftmanListOptions,
    filteredBranchOptions,
    filteredPartnerOptions,
    filteredTeamOptions,
    filteredWorkOrderOptions,
    setAppointmentTypeQuery,
    setBranchQuery,
    setCraftmanQuery,
    setPartnerQuery,
    setTeamQuery,
    setWorkOrderQuery,
    debouncedSetSearch,

    // Form handlers
    calculateMandatoryFields,
    onAppointmentTypeChange,
    onRemoveTeam,
    onRemovePerson,
    fetchDataFromWorkOrder,
  };
};
